import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import toast from 'react-hot-toast';
import api from '../../services/api';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        '&.Mui-focused fieldset': { borderColor: '#701280' },
    },
    '& label.Mui-focused': { color: '#701280' },
};

export default function ChangePasswordModal({ open, onClose }) {
    const { t } = useTranslation();

    const [current, setCurrent] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const handleClose = () => {
        if (loading) return;
        setCurrent(''); setNewPwd(''); setConfirm(''); setError('');
        onClose();
    };

    const handleSubmit = async () => {
        setError('');
        if (!current || !newPwd || !confirm) {
            setError(t('profile.changePwd.required'));
            return;
        }
        if (newPwd.length < 8) {
            setError(t('profile.changePwd.tooShort'));
            return;
        }
        if (newPwd !== confirm) {
            setError(t('profile.changePwd.mismatch'));
            return;
        }
        setLoading(true);
        try {
            await api.post('/accounts/direct-change-password/', {
                current_password: current,
                new_password: newPwd,
                confirm_password: confirm,
            });
            toast.success(t('profile.changePwd.success'));
            handleClose();
        } catch (e) {
            const code = e?.response?.data?.error;
            if (code === 'current_wrong') setError(t('profile.changePwd.wrongCurrent'));
            else if (code === 'too_short') setError(t('profile.changePwd.tooShort'));
            else if (code === 'mismatch') setError(t('profile.changePwd.mismatch'));
            else setError(t('profile.changePwd.genericError'));
        } finally {
            setLoading(false);
        }
    };

    const eyeBtn = (show, toggle) => (
        <InputAdornment position="end">
            <IconButton onClick={toggle} edge="end" size="small" tabIndex={-1}>
                {show ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { borderRadius: '20px', p: 1, maxWidth: 420, width: '100%' } }}
        >
            <DialogTitle sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1C1C28', pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '10px', background: 'rgba(112,18,128,0.08)' }}>
                    <LockOutlinedIcon sx={{ color: '#701280', fontSize: '1.1rem' }} />
                </Box>
                {t('profile.changePwd.title')}
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                    <TextField
                        label={t('profile.changePwd.current')}
                        type={showCurrent ? 'text' : 'password'}
                        value={current}
                        onChange={e => setCurrent(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="current-password"
                        sx={fieldSx}
                        InputProps={{ endAdornment: eyeBtn(showCurrent, () => setShowCurrent(v => !v)) }}
                    />
                    <TextField
                        label={t('profile.changePwd.new')}
                        type={showNew ? 'text' : 'password'}
                        value={newPwd}
                        onChange={e => setNewPwd(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                        sx={fieldSx}
                        InputProps={{ endAdornment: eyeBtn(showNew, () => setShowNew(v => !v)) }}
                    />
                    <TextField
                        label={t('profile.changePwd.confirm')}
                        type={showConfirm ? 'text' : 'password'}
                        value={confirm}
                        onChange={e => setConfirm(e.target.value)}
                        fullWidth
                        size="small"
                        autoComplete="new-password"
                        sx={fieldSx}
                        InputProps={{ endAdornment: eyeBtn(showConfirm, () => setShowConfirm(v => !v)) }}
                        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                    />
                    {error && (
                        <Box sx={{ fontSize: '0.8rem', color: '#EF4444', background: 'rgba(239,68,68,0.07)', borderRadius: '8px', px: 1.5, py: 1 }}>
                            {error}
                        </Box>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ color: '#6B6B7B', borderRadius: '10px', fontWeight: 600, border: '1px solid rgba(0,0,0,0.12)', px: 2.5 }}
                >
                    {t('profile.changePwd.cancel')}
                </Button>
                <Button
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
                    sx={{
                        background: 'linear-gradient(135deg, #701280, #9B1DB3)',
                        color: '#fff',
                        borderRadius: '10px',
                        fontWeight: 600,
                        px: 2.5,
                        '&:hover': { background: '#5a0e69' },
                        '&.Mui-disabled': { opacity: 0.6, color: '#fff' },
                    }}
                >
                    {loading ? t('profile.changePwd.saving') : t('profile.changePwd.submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
